import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faEdit, faTrash, faCopy, faSyncAlt, faExclamationTriangle, faFileDownload
} from '@fortawesome/free-solid-svg-icons';
import {
    Card, Typography, Button, Input, Select, Option, Dialog, DialogHeader,
    DialogBody, DialogFooter, Textarea, Progress, Chip
} from "@material-tailwind/react";
import Alerty from '../../common/Alerty';
import { exportBudgetsToCSV } from '../../../utils/expenseExport';
const { rootUrl } = require('../../../config/config');

const ExpenseBudgetManagement = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showBudgetForm, setShowBudgetForm] = useState(false);
    const [editingBudget, setEditingBudget] = useState(null);

    // Alert state
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ color: '', message: '' });

    // Form data
    const [formData, setFormData] = useState({
        budget_year: new Date().getFullYear(),
        budget_week: '',
        category_id: '',
        budgeted_amount: '',
        alert_threshold: 80,
        notes: ''
    });
    const [errors, setErrors] = useState({});

    // Filters
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        week: '',
        category_id: ''
    });

    // Reference data
    const [categories, setCategories] = useState([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchCategories();
        fetchBudgets();
    }, [filters]);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${rootUrl}/api/expenses/categories`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            if (response.data.success) {
                setCategories(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchBudgets = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/expenses/budgets/weekly`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
                params: filters
            });

            if (response.data.success) {
                setBudgets(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching budgets:', error);
            showAlert('error', 'Failed to fetch budgets');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.budget_year) newErrors.budget_year = 'Year is required';
        if (!formData.budget_week) newErrors.budget_week = 'Week is required';
        if (!formData.category_id) newErrors.category_id = 'Category is required';
        if (!formData.budgeted_amount || parseFloat(formData.budgeted_amount) <= 0) {
            newErrors.budgeted_amount = 'Budget amount must be greater than 0';
        }
        if (formData.alert_threshold < 0 || formData.alert_threshold > 100) {
            newErrors.alert_threshold = 'Alert threshold must be between 0 and 100';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const endpoint = editingBudget
                ? `${rootUrl}/api/expenses/budgets/weekly/${editingBudget.budget_id}`
                : `${rootUrl}/api/expenses/budgets/weekly`;

            const method = editingBudget ? 'put' : 'post';

            const response = await axios[method](endpoint, formData, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                showAlert('success', editingBudget ? 'Budget updated successfully' : 'Budget created successfully');
                setShowBudgetForm(false);
                setEditingBudget(null);
                setFormData({
                    budget_year: new Date().getFullYear(),
                    budget_week: '',
                    category_id: '',
                    budgeted_amount: '',
                    alert_threshold: 80,
                    notes: ''
                });
                fetchBudgets();
            }
        } catch (error) {
            console.error('Error saving budget:', error);
            showAlert('error', error.response?.data?.message || 'Failed to save budget');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (budget) => {
        setEditingBudget(budget);
        setFormData({
            budget_year: budget.budget_year,
            budget_week: budget.budget_week,
            category_id: budget.category_id,
            budgeted_amount: budget.budgeted_amount,
            alert_threshold: budget.alert_threshold || 80,
            notes: budget.notes || ''
        });
        setShowBudgetForm(true);
    };

    const handleDelete = async (budgetId) => {
        if (!window.confirm('Are you sure you want to delete this budget?')) return;

        try {
            const response = await axios.delete(`${rootUrl}/api/expenses/budgets/weekly/${budgetId}`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                showAlert('success', 'Budget deleted successfully');
                fetchBudgets();
            }
        } catch (error) {
            console.error('Error deleting budget:', error);
            showAlert('error', error.response?.data?.message || 'Failed to delete budget');
        }
    };

    const handleCopyBudgets = async () => {
        const fromWeek = prompt('Copy budgets from which week? (e.g., 1-52)');
        if (!fromWeek) return;

        const toWeek = prompt('Copy budgets to which week? (e.g., 1-52)');
        if (!toWeek) return;

        try {
            const response = await axios.post(
                `${rootUrl}/api/expenses/budgets/weekly/copy`,
                {
                    from_year: filters.year,
                    from_week: parseInt(fromWeek),
                    to_year: filters.year,
                    to_week: parseInt(toWeek)
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                showAlert('success', `Copied ${response.data.data.copied} budgets successfully`);
                fetchBudgets();
            }
        } catch (error) {
            console.error('Error copying budgets:', error);
            showAlert('error', error.response?.data?.message || 'Failed to copy budgets');
        }
    };

    const showAlert = (color, message) => {
        setAlert({ color, message });
        setOpen(true);
        setTimeout(() => setOpen(false), 5000);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount || 0);
    };

    const getBudgetStatus = (spentPercentage) => {
        if (spentPercentage >= 100) return { color: 'red', status: 'Exceeded' };
        if (spentPercentage >= 80) return { color: 'orange', status: 'Warning' };
        if (spentPercentage >= 60) return { color: 'amber', status: 'On Track' };
        return { color: 'green', status: 'Good' };
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-4">
            <Alerty open={open} color={alert.color} message={alert.message} setOpen={setOpen} />

            {/* Header */}
            <div className="mb-6">
                <Typography variant="h3" className="text-gray-800 font-bold mb-2">
                    Budget Management
                </Typography>
                <Typography variant="paragraph" className="text-gray-600">
                    Set and track weekly expense budgets by category
                </Typography>
            </div>

            {/* Action Buttons */}
            <Card className="mb-6 p-4">
                <div className="flex flex-wrap gap-3 items-center justify-between">
                    <div className="flex flex-wrap gap-3">
                        <Button
                            onClick={() => {
                                setEditingBudget(null);
                                setFormData({
                                    budget_year: new Date().getFullYear(),
                                    budget_week: '',
                                    category_id: '',
                                    budgeted_amount: '',
                                    alert_threshold: 80,
                                    notes: ''
                                });
                                setShowBudgetForm(true);
                            }}
                            className="bg-[#FF5733] hover:bg-[#ff4520] flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Add Budget
                        </Button>
                        <Button
                            onClick={handleCopyBudgets}
                            variant="outlined"
                            className="flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faCopy} />
                            Copy Budgets
                        </Button>
                        <Button
                            onClick={fetchBudgets}
                            variant="outlined"
                            className="flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} />
                            Refresh
                        </Button>
                        <Button
                            onClick={() => exportBudgetsToCSV(budgets)}
                            variant="outlined"
                            className="flex items-center gap-2"
                            disabled={budgets.length === 0}
                        >
                            <FontAwesomeIcon icon={faFileDownload} />
                            Export CSV
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                    <Input
                        type="number"
                        label="Year"
                        value={filters.year}
                        onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    />

                    <Input
                        type="number"
                        label="Week"
                        placeholder="All weeks"
                        value={filters.week}
                        onChange={(e) => setFilters({ ...filters, week: e.target.value })}
                    />

                    <Select
                        label="Category"
                        value={filters.category_id}
                        onChange={(val) => setFilters({ ...filters, category_id: val })}
                    >
                        <Option value="">All Categories</Option>
                        {categories
                            .filter((cat) => cat && cat.category_id !== undefined && cat.category_id !== null)
                            .map((cat) => (
                                <Option key={cat.category_id} value={String(cat.category_id)}>
                                    {cat.category_name}
                                </Option>
                            ))}
                    </Select>
                </div>
            </Card>

            {/* Budgets List */}
            <div className="grid grid-cols-1 gap-4">
                {loading ? (
                    <Card className="p-8 text-center">
                        <FontAwesomeIcon icon={faSyncAlt} spin size="2x" className="text-[#FF5733]" />
                        <Typography variant="paragraph" className="mt-2">Loading budgets...</Typography>
                    </Card>
                ) : budgets.length === 0 ? (
                    <Card className="p-8 text-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400" />
                        <Typography variant="paragraph" className="mt-2 text-gray-600">
                            No budgets found for the selected filters
                        </Typography>
                    </Card>
                ) : (
                    budgets.map((budget) => {
                        const spentPercentage = parseFloat(budget.spent_percentage) || 0;
                        const status = getBudgetStatus(spentPercentage);
                        const remaining = parseFloat(budget.budgeted_amount) - parseFloat(budget.spent_amount || 0);

                        return (
                            <Card key={budget.budget_id} className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <Typography variant="h5" className="text-gray-800 font-bold">
                                            {budget.category_name}
                                        </Typography>
                                        <Typography variant="small" className="text-gray-600">
                                            Week {budget.budget_week}, {budget.budget_year}
                                        </Typography>
                                    </div>
                                    <div className="flex gap-2">
                                        <Chip
                                            value={status.status}
                                            color={status.color}
                                        />
                                        <button
                                            onClick={() => handleEdit(budget)}
                                            className="text-blue-600 hover:text-blue-800 p-2"
                                            title="Edit"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(budget.budget_id)}
                                            className="text-red-600 hover:text-red-800 p-2"
                                            title="Delete"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <Typography variant="small" className="text-gray-600">
                                            Budgeted
                                        </Typography>
                                        <Typography variant="h6" className="text-gray-800 font-bold">
                                            {formatCurrency(budget.budgeted_amount)}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="small" className="text-gray-600">
                                            Spent
                                        </Typography>
                                        <Typography variant="h6" className="text-[#FF5733] font-bold">
                                            {formatCurrency(budget.spent_amount)}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="small" className="text-gray-600">
                                            Remaining
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            className={`font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                        >
                                            {formatCurrency(remaining)}
                                        </Typography>
                                    </div>
                                </div>

                                <div className="mb-2">
                                    <div className="flex justify-between mb-1">
                                        <Typography variant="small" className="text-gray-600">
                                            Budget Usage
                                        </Typography>
                                        <Typography variant="small" className={`font-bold text-${status.color}-600`}>
                                            {spentPercentage.toFixed(1)}%
                                        </Typography>
                                    </div>
                                    <Progress
                                        value={Math.min(spentPercentage, 100)}
                                        color={status.color}
                                        size="lg"
                                    />
                                </div>

                                {budget.notes && (
                                    <Typography variant="small" className="text-gray-600 mt-2">
                                        <strong>Notes:</strong> {budget.notes}
                                    </Typography>
                                )}
                            </Card>
                        );
                    })
                )}
            </div>

            {/* Budget Form Dialog */}
            <Dialog open={showBudgetForm} handler={() => setShowBudgetForm(false)} size="md">
                <DialogHeader className="border-b border-gray-200">
                    <Typography variant="h5" className="text-gray-800">
                        {editingBudget ? 'Edit Budget' : 'Add New Budget'}
                    </Typography>
                </DialogHeader>

                <DialogBody className="overflow-y-auto max-h-[60vh]">
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Input
                                    type="number"
                                    label="Year *"
                                    value={formData.budget_year}
                                    onChange={(e) => handleInputChange('budget_year', e.target.value)}
                                    error={!!errors.budget_year}
                                />
                                {errors.budget_year && (
                                    <Typography variant="small" color="red" className="mt-1">
                                        {errors.budget_year}
                                    </Typography>
                                )}
                            </div>

                            <div>
                                <Input
                                    type="number"
                                    label="Week (1-52) *"
                                    value={formData.budget_week}
                                    onChange={(e) => handleInputChange('budget_week', e.target.value)}
                                    error={!!errors.budget_week}
                                />
                                {errors.budget_week && (
                                    <Typography variant="small" color="red" className="mt-1">
                                        {errors.budget_week}
                                    </Typography>
                                )}
                            </div>
                        </div>

                        <div>
                            <Select
                                label="Category *"
                                value={formData.category_id?.toString()}
                                onChange={(val) => handleInputChange('category_id', val)}
                                error={!!errors.category_id}
                            >
                                {categories
                                    .filter((cat) => cat && cat.category_id !== undefined && cat.category_id !== null)
                                    .map((cat) => (
                                        <Option key={cat.category_id} value={String(cat.category_id)}>
                                            {cat.category_name} ({cat.expense_type})
                                        </Option>
                                    ))}
                            </Select>
                            {errors.category_id && (
                                <Typography variant="small" color="red" className="mt-1">
                                    {errors.category_id}
                                </Typography>
                            )}
                        </div>

                        <div>
                            <Input
                                type="number"
                                label="Budget Amount (KES) *"
                                value={formData.budgeted_amount}
                                onChange={(e) => handleInputChange('budgeted_amount', e.target.value)}
                                error={!!errors.budgeted_amount}
                            />
                            {errors.budgeted_amount && (
                                <Typography variant="small" color="red" className="mt-1">
                                    {errors.budgeted_amount}
                                </Typography>
                            )}
                        </div>

                        <div>
                            <Input
                                type="number"
                                label="Alert Threshold (%) *"
                                value={formData.alert_threshold}
                                onChange={(e) => handleInputChange('alert_threshold', e.target.value)}
                                error={!!errors.alert_threshold}
                            />
                            <Typography variant="small" className="text-gray-600 mt-1">
                                You'll receive alerts when spending exceeds this percentage
                            </Typography>
                            {errors.alert_threshold && (
                                <Typography variant="small" color="red" className="mt-1">
                                    {errors.alert_threshold}
                                </Typography>
                            )}
                        </div>

                        <div>
                            <Textarea
                                label="Notes"
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="border-t border-gray-200">
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowBudgetForm(false);
                            setEditingBudget(null);
                        }}
                        className="mr-2"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="bg-[#FF5733] hover:bg-[#ff4520]"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : editingBudget ? 'Update' : 'Save'}
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default ExpenseBudgetManagement;
